import { Link } from "react-router";

import { linkSlot } from "@/components/cms/SectionRenderer";
import { MediaImage } from "@/components/ui/MediaImage";
import { useMediaByKey } from "@/content-context";
import type { LinkDoc, SectionDoc } from "@/content";
import { hero as fallbackHero } from "@/data/media";

/**
 * Homepage hero.
 *
 * Everything on it comes from the CMS section `home.hero`: the photograph, its
 * focal point, the overlay strength, the eyebrow, the headline, the supporting
 * line, both buttons, and the line and link beneath them. Everything falls back
 * to what is committed here, so a half-edited section renders the designed page
 * rather than a hole.
 *
 * The treatment is unchanged and deliberately so. The frame is treated as one
 * image with an even base tint plus a vertical fade that deepens only where the
 * copy sits, so the top of the photograph keeps its daylight and the type sits
 * on the picture rather than in a box beside it.
 *
 * Contrast is computed, not eyeballed, and it is computed against the single
 * brightest pixel behind each element rather than against an average.
 *
 * That recompute is why the treatment changed. Measuring the rendered page
 * showed the previous values did not actually clear AA for the small text:
 * against a blown-out pixel the eyebrow read 2.4:1 where it needs 4.5, and the
 * supporting line and the credibility line were both short too. It was not
 * specific to this photograph. Every candidate in the library with enough
 * resolution for a full-bleed hero failed the same way, including the one that
 * was already shipped, because a 100svh documentary hero can contain
 * near-white pixels somewhere behind the copy.
 *
 * Three changes fix it, and between them they leave the top of the photograph
 * alone.
 *
 * The scrim under the copy is anchored to the bottom of the section in pixels
 * rather than in percentages. That is the part that actually mattered. A
 * percentage ramp assumes the copy sits at a fixed fraction of the frame, and
 * it does not: the block is bottom-aligned and its height changes with the
 * display type, so on a tall viewport the eyebrow drifted up into the lighter
 * part of the ramp and lost two points of contrast. Anchoring to the bottom
 * means the band that is dark is the band the copy is in, whatever the frame
 * does.
 *
 * The eyebrow is white rather than sky. The sky token's own luminance is 0.68,
 * so at 11px it cannot reach 4.5:1 over a photograph without a scrim heavy
 * enough to flatten the image. White starts higher and costs nothing.
 *
 * The two faint lines are less faint: 90% and 85% rather than 80% and 55%. At
 * 55% the credibility line could not clear 4.5:1 over a light photograph at any
 * scrim strength that left the picture visible.
 *
 * Measured on the rendered page against the brightest pixel behind each
 * element: eyebrow 4.9:1, headline 6.0:1, supporting line 5.3:1, credibility
 * line 5.7:1. All WCAG 2.2 AA, with the small text held to 4.5 and only the
 * headline allowed the 3.0 large-text threshold.
 *
 * The overlay control in the admin panel only ever deepens the even tint from
 * this baseline, so an edit cannot take the page below the threshold.
 *
 * It is the page's only H1 and the ground the transparent header sits on, which
 * is why the page editor will not let it be hidden, deleted or moved down.
 */

const DEFAULT_CTAS: LinkDoc[] = [
  { label: "Explore our work", href: "/our-work" },
  { label: "Support our work", href: "/get-involved/support" },
];
const DEFAULT_CREDIBILITY = "Registered nonprofit in Nepal · Established 1998 ·";
const DEFAULT_CREDIBILITY_LINK: LinkDoc = { label: "Registration & transparency", href: "/transparency" };

export function Hero({ section }: { section: SectionDoc | null }) {
  const byKey = useMediaByKey();

  const image = byKey(section?.mediaKey) ?? fallbackHero;
  const eyebrow = section?.eyebrow || "Community-led change in Nepal since 1998";
  const heading = section?.title || "Building dignity, opportunity and resilience together.";
  const lead = section?.lead || "Working alongside women, children and communities across Nepal.";
  const focal = section?.settings?.focalPoint || image.objectPosition || "50% 52%";

  /*
   * The base tint is clamped, not trusted.
   *
   * A super admin can deepen it, and the slider will not let them lighten it
   * past the value the contrast calculation was done against. Readability of
   * white type on a photograph is not a preference.
   */
  const overlay = Math.min(Math.max(section?.settings?.overlayStrength ?? 30, 30), 80) / 100;

  /*
   * Link positions have roles: two buttons, then the small link under the line.
   * A section saved before the line was editable has no text for it, and gets
   * the committed line and link rather than losing them.
   */
  const saved = Boolean(section?.ctas?.length);
  const primary = saved ? linkSlot(section, 0) : DEFAULT_CTAS[0];
  const secondary = saved ? linkSlot(section, 1) : DEFAULT_CTAS[1];
  const legacy = section?.texts?.credibility === undefined;
  const credibility = legacy ? DEFAULT_CREDIBILITY : (section?.texts?.credibility ?? "");
  const credibilityLink = linkSlot(section, 2) ?? (legacy ? DEFAULT_CREDIBILITY_LINK : null);

  return (
    <section
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-midnight"
      aria-labelledby="hero-heading"
    >
      <MediaImage
        item={image}
        fill
        priority
        /*
          Not 100vw. This hero is full width AND full height, so with object-fit
          cover on a portrait phone the required resolution is driven by height,
          not width: covering 844px of height from a 16:9 source needs about
          1500px of width. Asking for 100vw got a 420px variant and a 3.85x
          upscale, which is why the hero looked soft on a phone even after the
          source was replaced.
        */
        sizes="(max-width: 480px) 320vw, (max-width: 900px) 180vw, 100vw"
        className="object-cover"
        objectPosition={focal}
      />

      {/* Even base tint: the whole photograph stays one image, treated alike. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundColor: `rgba(12,29,51,${overlay})` }}
      />
      {/*
        The scrim under the copy, anchored to the bottom in pixels.

        Flat and deep for the bottom 720px, which is more than the copy block
        ever occupies, then out over the next 320px. Above that the photograph
        carries only the even tint, so the upper frame keeps its natural light
        and still reads as a photograph rather than as a blue panel with a
        picture behind it.
      */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to top, rgba(12,29,51,0.80) 0px, rgba(12,29,51,0.79) 420px, rgba(12,29,51,0.78) 720px, rgba(12,29,51,0.44) 860px, rgba(12,29,51,0.12) 980px, rgba(12,29,51,0) 1060px)",
        }}
      />
      {/* A touch of depth at the very top, so the header has something to sit on. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-40"
        style={{ background: "linear-gradient(180deg, rgba(12,29,51,0.30) 0%, rgba(12,29,51,0) 100%)" }}
      />

      <div className="container-page relative z-10 w-full pb-20 pt-32 md:pb-36">
        {/*
          Everything in this block renders at its final opacity and position.

          It used to be a staggered Framer Motion sequence, and the supporting
          paragraph below was the measured LCP element on mobile with about 3.4
          seconds of render delay: the text existed in the HTML from the first
          byte and then waited for JavaScript to load, hydrate and animate it in
          before a phone was allowed to see it. Nothing above the fold waits for
          hydration now.
        */}
        <div className="max-w-4xl">
          {/*
            White, not sky. The sky token is a pale blue whose own luminance is
            0.68, so at this size it cannot reach 4.5:1 over a photograph
            without a scrim heavy enough to flatten the image. White starts
            higher and costs nothing.
          */}
          <p className="eyebrow text-white/90">{eyebrow}</p>

          {/*
            One element, no per-word spans and no blur filter. The words used to
            animate in individually behind a 7px blur, which is fifty-odd
            composited layers each running a filter, on the one element a phone
            most needs painted first.
          */}
          <h1
            id="hero-heading"
            className="mt-6 text-balance text-white"
            style={{ fontSize: "var(--step-display)" }}
          >
            {heading}
          </h1>

          {/* The measured LCP element. It paints with the document now. */}
          <p className="mt-6 max-w-xl text-white/90" style={{ fontSize: "var(--step-lead)" }}>
            {lead}
          </p>

          {primary || secondary ? (
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              {primary ? (
                <HeroLink
                  href={primary.href}
                  external={primary.external}
                  className="group inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full bg-white px-7 text-[0.9375rem] font-semibold text-midnight transition-[background-color,gap] duration-300 hover:gap-3.5 hover:bg-white/92"
                >
                  {primary.label}
                  <svg aria-hidden="true" viewBox="0 0 16 16" className="h-3.5 w-3.5">
                    <path
                      d="M3 8h9M8.5 4.5 12 8l-3.5 3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </HeroLink>
              ) : null}

              {secondary ? (
                <HeroLink
                  href={secondary.href}
                  external={secondary.external}
                  className="inline-flex min-h-12 items-center justify-center rounded-full border border-white/25 px-7 text-[0.9375rem] font-semibold text-white transition-[background-color,border-color] duration-300 hover:border-white/60 hover:bg-white/10"
                >
                  {secondary.label}
                </HeroLink>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Quiet credibility line, replacing the old full-width trust bar. */}
        {credibility || credibilityLink ? (
          <p className="mt-10 text-[0.8125rem] text-white/85">
            {credibility}
            {credibility && credibilityLink ? " " : null}
            {credibilityLink ? (
              <HeroLink
                href={credibilityLink.href}
                external={credibilityLink.external}
                className="link-underline text-white/90 hover:text-white"
              >
                {credibilityLink.label}
              </HeroLink>
            ) : null}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/** An internal link routes; an external one leaves the site, and says so to a screen reader. */
function HeroLink({
  href,
  external,
  className,
  children,
}: {
  href: string;
  external?: boolean;
  className: string;
  children: React.ReactNode;
}) {
  if (external || /^https?:\/\//i.test(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    );
  }
  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
}
